import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Box } from '@/UI/Box/Box';
import { CircularProgress } from '@/UI/CircularProgress/CircularProgress';
import { AppTable } from '@/UI/AppTable/AppTable';
import { getTimeDiffInWords } from '@/utils/date.utils';
import { toMillis } from '@/utils/folder.utils';
import { color } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { FlexCenHorVer } from '@/UI/styles';
import { I18N_KEYS } from '@/language/keys';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { LabelSmRg, TextSmRg } from '@/UI/AppTypography/AppTypography';
import { EllipsisText } from '@/UI/EllipsisText/EllipsisText';
import { FolderThumbnail } from '../components/FolderThumbnail';
import { descendingComparator } from '../utils/listView.utils';
import { defaultWorkflowCover } from '../utils/defaultCover';
import type { ArchiveItem } from './archive.types';
import type { AllowedArchiveKeys, ArchiveEnhancedTableHeadProps, ArchiveHeadCell } from '../types/listView.types';
import type { FolderWorkflowData } from '../utils/listView.utils';
import type { MouseEvent } from 'react';

function EnhancedTableHead({ order, orderBy, onRequestSort }: ArchiveEnhancedTableHeadProps) {
  const { t } = useTranslation();

  const createSortHandler = (property: AllowedArchiveKeys) => (event: MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  const headCells: ArchiveHeadCell[] = [
    {
      id: 'thumbnail',
      numeric: false,
      disablePadding: true,
      label: t(I18N_KEYS.GENERAL.NAME),
      sortKey: 'name',
    },
    {
      id: 'name',
      numeric: false,
      disablePadding: true,
      label: '',
      disableSort: true,
    },
    {
      id: 'files',
      numeric: true,
      disablePadding: false,
      label: t(I18N_KEYS.GENERAL.FILES),
    },
    {
      id: 'deletedAt',
      numeric: true,
      disablePadding: false,
      label: t(I18N_KEYS.DASHBOARD.PAGES.ARCHIVE.DELETED_AT_HEADER),
    },
  ];

  return (
    <AppTable.Thead>
      <AppTable.Tr>
        {headCells.map((headCell) => {
          const sortId = headCell.sortKey ?? headCell.id;
          return (
            <AppTable.Th
              key={headCell.id}
              align={headCell.numeric ? 'center' : 'left'}
              padding={headCell.disablePadding ? 'none' : 'normal'}
              sortDirection={orderBy === sortId ? order : false}
            >
              {headCell.disableSort ? (
                <LabelSmRg text={headCell.label} color={token.text} />
              ) : (
                <AppTable.SortLabel
                  active={orderBy === sortId}
                  direction={orderBy === sortId ? order : 'asc'}
                  onClick={createSortHandler(sortId)}
                  sx={{ justifyContent: headCell.numeric ? 'center' : 'flex-start' }}
                >
                  <LabelSmRg text={headCell.label} color={token.text} />
                </AppTable.SortLabel>
              )}
            </AppTable.Th>
          );
        })}
      </AppTable.Tr>
    </AppTable.Thead>
  );
}

interface ArchiveListViewProps {
  items: ArchiveItem[];
  onRestore: (id: string, type: 'workflow' | 'folder') => void;
  restoringIds?: Set<string>;
  folderFileCounts: Map<string, number>;
  archiveFolderData: FolderWorkflowData;
}

const ArchiveListView = ({
  items,
  onRestore,
  restoringIds,
  folderFileCounts,
  archiveFolderData,
}: ArchiveListViewProps) => {
  const { t } = useTranslation();
  const isFplMigrationEnabled = useFplMigrationFlag();
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<AllowedArchiveKeys>('deletedAt');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(7);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const handleRequestSort = (_e: MouseEvent<unknown>, property: AllowedArchiveKeys) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (newPage: number) => setPage(newPage);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(items.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [items.length, page, rowsPerPage]);

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - items.length) : 0;

  const visibleRows = useMemo(() => {
    const sorted = [...items].sort((a, b) => {
      if (orderBy === 'deletedAt') {
        const timeA = toMillis(a.deletedAt) || 0;
        const timeB = toMillis(b.deletedAt) || 0;
        return order === 'desc' ? timeB - timeA : timeA - timeB;
      } else if (orderBy === 'files') {
        // Workflows (shown as "-") always sort after folders regardless of direction
        if (a.itemType !== 'folder' && b.itemType !== 'folder') return 0;
        if (a.itemType !== 'folder') return 1;
        if (b.itemType !== 'folder') return -1;
        const filesA = folderFileCounts.get(a.id) ?? 0;
        const filesB = folderFileCounts.get(b.id) ?? 0;
        return order === 'desc' ? filesB - filesA : filesA - filesB;
      } else {
        const result = descendingComparator(a, b, orderBy as keyof ArchiveItem);
        return order === 'desc' ? result : -result;
      }
    });
    return sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [items, order, orderBy, page, rowsPerPage, folderFileCounts]);

  if (!items.length) {
    return null;
  }

  return (
    <Box data-testid="archive-table-view-container">
      <AppTable.TableContainer>
        <AppTable
          aria-labelledby="archiveTableTitle"
          size="medium"
          sx={{
            '& .MuiTableCell-root': {
              borderBottom: 'none',
            },
          }}
        >
          <EnhancedTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
          <AppTable.Tbody>
            {visibleRows.map((item) => {
              const isHovered = hoveredId === item.id;

              return (
                <AppTable.Tr
                  hover
                  key={item.id}
                  tabIndex={-1}
                  onMouseEnter={() => setHoveredId(item.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  sx={{ cursor: 'default', border: 'none' }}
                >
                  <AppTable.Td padding="none" sx={{ p: 1, borderRadius: '8px 0 0 8px', width: 160 }}>
                    <Box
                      sx={{
                        width: 144,
                        height: 72,
                        borderRadius: 2,
                        position: 'relative',
                        border: `1px solid ${isHovered ? color.White08_T : 'transparent'}`,
                        overflow: 'hidden',
                        backgroundColor: item.itemType === 'folder' ? token.bg : 'none',
                      }}
                    >
                      {item.itemType === 'folder' ? (
                        <FolderThumbnail
                          posters={archiveFolderData[item.id]?.posters ?? []}
                          count={archiveFolderData[item.id]?.count ?? 0}
                        />
                      ) : (
                        <FlexCenHorVer sx={{ width: '100%', height: '100%' }}>
                          <img
                            style={{
                              display: 'block',
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            src={item.poster || defaultWorkflowCover(isFplMigrationEnabled, 'list')}
                            alt="workflow poster"
                          />
                        </FlexCenHorVer>
                      )}
                    </Box>
                  </AppTable.Td>
                  <AppTable.Td
                    component="th"
                    scope="row"
                    padding="none"
                    sx={{
                      width: { xs: 120, sm: 200, md: 300, lg: 400, xl: 500 },
                      maxWidth: { xs: 120, sm: 200, md: 300, lg: 400, xl: 500 },
                    }}
                  >
                    <EllipsisText variant="body-sm-md" isMultiLine>
                      {item.name}
                    </EllipsisText>
                  </AppTable.Td>
                  <AppTable.Td align="center" sx={{ width: 100 }}>
                    <TextSmRg
                      text={
                        item.itemType === 'folder'
                          ? `${folderFileCounts.get(item.id) ?? 0} ${
                              folderFileCounts.get(item.id) === 1
                                ? t(I18N_KEYS.GENERAL.FILE)
                                : t(I18N_KEYS.GENERAL.FILES)
                            }`
                          : '-'
                      }
                    />
                  </AppTable.Td>
                  <AppTable.Td align="center" sx={{ borderRadius: '0 8px 8px 0', width: 160, px: 0.5 }}>
                    {isHovered || restoringIds?.has(item.id) ? (
                      <ButtonContained
                        size="small"
                        disabled={restoringIds?.has(item.id)}
                        startIcon={
                          restoringIds?.has(item.id) && <CircularProgress size={14} sx={{ color: 'inherit' }} />
                        }
                        onClick={() => onRestore(item.id, item.itemType)}
                      >
                        {t(I18N_KEYS.DASHBOARD.PAGES.ARCHIVE.RESTORE)}
                      </ButtonContained>
                    ) : (
                      <TextSmRg
                        text={t(I18N_KEYS.DASHBOARD.PAGES.ARCHIVE.DELETED_AT, {
                          date: getTimeDiffInWords(toMillis(item.deletedAt)),
                        })}
                      />
                    )}
                  </AppTable.Td>
                </AppTable.Tr>
              );
            })}
            {emptyRows > 0 && (
              <AppTable.Tr style={{ height: 53 * emptyRows }}>
                <AppTable.Td colSpan={4} />
              </AppTable.Tr>
            )}
          </AppTable.Tbody>
        </AppTable>
      </AppTable.TableContainer>
      <AppTable.Pagination
        rowsPerPageOptions={[]}
        component="div"
        count={items.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => handleChangePage(newPage)}
      />
    </Box>
  );
};

export default ArchiveListView;
